import React, { useState, useEffect } from 'react';
import { Search, Package, DollarSign, TrendingUp, ArrowRight, Users, Trash2, X, CheckCircle, XCircle } from 'lucide-react';
import apiService from '../services/api';

// Toast Component - Minimalista
const Toast = ({ message, type, onClose }) => {
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const duration = 4000;
    const interval = 10;
    const decrement = (100 * interval) / duration;

    const progressTimer = setInterval(() => {
      setProgress((prev) => {
        if (prev <= 0) {
          clearInterval(progressTimer);
          return 0;
        }
        return prev - decrement;
      });
    }, interval);

    const closeTimer = setTimeout(() => {
      onClose();
    }, duration);

    return () => {
      clearInterval(progressTimer);
      clearTimeout(closeTimer);
    };
  }, [onClose]);

  const config = {
    success: {
      icon: <CheckCircle size={22} />,
      borderColor: 'border-green-500',
      iconColor: 'text-green-500',
      progressColor: 'bg-green-500'
    },
    error: {
      icon: <XCircle size={22} />,
      borderColor: 'border-red-500',
      iconColor: 'text-red-500',
      progressColor: 'bg-red-500'
    }
  };

  const currentConfig = config[type] || config.success;

  return (
    <div className={`bg-white ${currentConfig.borderColor} border-l-4 rounded-lg shadow-lifted overflow-hidden min-w-[320px] animate-slideIn`}>
      <div className="px-5 py-4 flex items-start gap-3">
        <div className={`flex-shrink-0 ${currentConfig.iconColor}`}>{currentConfig.icon}</div>
        <div className="flex-1">
          <p className="font-medium text-sm text-ink-900">{message}</p>
        </div>
        <button onClick={onClose} className="hover:bg-ink-100 rounded p-1 text-ink-500">
          <X size={18} />
        </button>
      </div>
      <div className="h-1 bg-ink-100">
        <div className={`h-full ${currentConfig.progressColor} transition-all`} style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
};

const ToastContainer = ({ toasts, removeToast }) => (
  <div className="fixed top-4 right-4 z-50 space-y-3">
    {toasts.map((toast) => (
      <Toast key={toast.id} message={toast.message} type={toast.type} onClose={() => removeToast(toast.id)} />
    ))}
  </div>
);

// Confirm Dialog - Minimalista
const ConfirmDialog = ({ isOpen, title, message, onConfirm, onCancel, clientName }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-ink-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lifted max-w-md w-full animate-scale-in">
        <div className="px-6 py-4 border-b border-ink-200">
          <h3 className="text-lg font-semibold text-ink-900">{title}</h3>
        </div>
        <div className="px-6 py-4">
          <p className="text-ink-600 mb-3">{message}</p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-800">
              <strong>Cliente:</strong> {clientName}
            </p>
            <p className="text-xs text-yellow-700 mt-2">
              ⚠️ Este cliente dejará de aparecer en el sistema, pero podrás recuperarlo desde el módulo de Administración.
            </p>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-ink-200 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-ink-300 text-ink-700 rounded-lg hover:bg-ink-50 transition"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
          >
            Eliminar Cliente
          </button>
        </div>
      </div>
    </div>
  );
};

function Clientes({ onClienteClick }) {
  const [clientes, setClientes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [toasts, setToasts] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [clienteToDelete, setClienteToDelete] = useState(null);

  useEffect(() => {
    fetchClientes();
  }, []);

  const fetchClientes = async () => {
    try {
      const data = await apiService.get('/clientes');
      setClientes(data);
    } catch (error) {
      console.error('Error al cargar clientes:', error);
      if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
        showToast('Error al cargar clientes', 'error');
      }
    }
  };

  const showToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const handleDeleteClick = (cliente) => {
    setClienteToDelete(cliente);
    setShowConfirmDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await apiService.delete(`/clientes/${encodeURIComponent(clienteToDelete.nombre)}`);
      showToast(`Cliente "${clienteToDelete.nombre}" eliminado exitosamente`, 'success');
      fetchClientes();
    } catch (error) {
      console.error('Error:', error);
      if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
        showToast(error.message || 'Error al conectar con el servidor', 'error');
      }
    }

    setShowConfirmDialog(false);
    setClienteToDelete(null);
  };

  const filteredClientes = clientes
    .filter(cliente => cliente.totalContenedores > 0)
    .filter(cliente =>
      cliente.nombre?.toLowerCase().includes(searchTerm.toLowerCase())
    );

  return (
    <div className="p-8 bg-ink-50 min-h-screen">
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      <ConfirmDialog
        isOpen={showConfirmDialog}
        title="¿Eliminar cliente?"
        message="Esta acción ocultará el cliente del sistema. Los contenedores asociados también se ocultarán, pero podrás recuperarlos desde Administración."
        clientName={clienteToDelete?.nombre}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setShowConfirmDialog(false);
          setClienteToDelete(null);
        }}
      />

      <div className="mb-8 animate-fade-in">
        <h1 className="font-display text-3xl text-ink-900 mb-1">Clientes</h1>
        <p className="text-ink-500">Gestiona tus clientes y visualiza sus contenedores</p>
      </div>

      <div className="mb-6 animate-fade-in">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-400" size={20} />
          <input
            type="text"
            placeholder="Buscar clientes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-white border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-900 focus:border-transparent text-ink-900 placeholder-ink-400"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-4 rounded-2xl bg-gradient-to-br from-blue-50/50 via-transparent to-violet-50/50">
        {filteredClientes.map((cliente, index) => (
          <div
            key={index}
            className="bg-white/70 backdrop-blur-md rounded-xl shadow-soft hover:shadow-lifted hover:bg-white/90 transition-all duration-300 p-6 border border-white/50 relative group animate-fade-in"
          >
            {/* Botón eliminar */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(cliente);
              }}
              className="absolute top-4 right-4 p-2 text-ink-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition opacity-0 group-hover:opacity-100"
              title="Eliminar cliente"
            >
              <Trash2 size={18} />
            </button>

            <div
              className="cursor-pointer"
              onClick={() => onClienteClick(cliente.nombre)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-violet-100 rounded-full flex items-center justify-center ring-2 ring-white/50 shadow-sm">
                  <span className="text-blue-700 font-bold text-2xl">
                    {cliente.nombre?.charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>

              <h3 className="text-xl font-bold text-ink-900 mb-4">{cliente.nombre}</h3>

              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between p-3 bg-white/60 backdrop-blur-sm rounded-lg border border-ink-100">
                  <div className="flex items-center gap-2">
                    <Package size={18} className="text-blue-600" />
                    <span className="text-sm text-ink-600">Contenedores</span>
                  </div>
                  <span className="font-bold text-blue-700">{cliente.totalContenedores}</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-green-50/70 backdrop-blur-sm rounded-lg border border-green-100">
                  <div className="flex items-center gap-2">
                    <DollarSign size={18} className="text-green-600" />
                    <span className="text-sm text-ink-600">Ingresos</span>
                  </div>
                  <span className="font-bold text-green-600">${cliente.totalVenta.toFixed(2)}</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-violet-50/50 backdrop-blur-sm rounded-lg border border-violet-100">
                  <div className="flex items-center gap-2">
                    <TrendingUp size={18} className="text-violet-600" />
                    <span className="text-sm text-ink-600">Utilidad</span>
                  </div>
                  <span className="font-bold text-violet-700">${cliente.utilidad.toFixed(2)}</span>
                </div>
              </div>

              <button
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-all duration-300 font-medium shadow-sm hover:shadow-md"
              >
                <span>Ver Contenedores</span>
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredClientes.length === 0 && (
        <div className="text-center py-16 animate-fade-in">
          <Users size={64} className="mx-auto text-ink-300 mb-4" />
          <h3 className="text-xl font-semibold text-ink-900 mb-2">No hay clientes</h3>
          <p className="text-ink-600 mb-6">
            {searchTerm
              ? 'No se encontraron clientes con ese criterio'
              : 'Importa un Excel con la columna CLIENTE para ver tus clientes aquí'}
          </p>
        </div>
      )}
    </div>
  );
}

export default Clientes;
